import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * API hooks for mini apps (customer-facing as "Apps", API namespace "miniapps").
 */

/**
 * App status values from the API
 */
export type AppStatus = "draft" | "active" | "disabled" | "archived";

/**
 * App (MiniApp) entity from the API
 */
export interface App {
  id: string;
  display_name: string;
  description?: string | null;
  status: AppStatus;
  current_version_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Embed info returned from the embed endpoint
 */
export interface AppEmbedInfo {
  embed_url: string;
  expires_in_seconds: number;
  csp: string | null;
}

/**
 * Request body for updating an app
 */
export interface UpdateAppRequest {
  status?: AppStatus;
}

export const APPS_QUERY_KEY = ["apps"] as const;

/**
 * API response wrapper for list endpoint
 */
interface ListAppsResponse {
  apps: App[];
}

/**
 * Hook for listing all apps (mini apps) for the current organization.
 * By default, returns only active apps unless status filter is provided.
 */
export function useApps(statusFilter?: AppStatus) {
  const params = statusFilter ? { status: statusFilter } : undefined;

  return useQuery<App[]>({
    queryKey: [...APPS_QUERY_KEY, statusFilter ?? "active"],
    queryFn: async () => {
      const response = await api.get<ListAppsResponse>("miniapps/", {
        params,
      });
      return response.apps;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * API response wrapper for get endpoint
 */
interface GetAppResponse {
  app: App;
}

/**
 * Hook for fetching a single app's metadata by ID.
 */
export function useApp(appId: string | undefined) {
  return useQuery<App>({
    queryKey: [...APPS_QUERY_KEY, appId],
    queryFn: async () => {
      if (!appId) throw new Error("App ID is required");
      const response = await api.get<GetAppResponse>(`miniapps/${appId}/`);
      return response.app;
    },
    enabled: !!appId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * API response wrapper for embed endpoint (matches protobuf GetReportEmbedResponse)
 */
interface GetAppEmbedResponse {
  embed: AppEmbedInfo;
}

/**
 * Hook for fetching the embed URL with JWT token for an app.
 * The embed URL includes authentication and expires after a set time.
 */
export function useAppEmbed(appId: string | undefined) {
  return useQuery<AppEmbedInfo>({
    queryKey: [...APPS_QUERY_KEY, appId, "embed"],
    queryFn: async () => {
      if (!appId) throw new Error("App ID is required");
      const response = await api.get<GetAppEmbedResponse>(
        `miniapps/${appId}/embed/`,
      );
      return response.embed;
    },
    enabled: !!appId,
    // Refetch before token expires (token typically expires in 15 min)
    // Refetch at 12 minutes to ensure fresh token
    staleTime: 12 * 60 * 1000,
    refetchInterval: (query) => {
      // Refetch 60 seconds before expiry, or every 12 minutes as fallback
      const data = query.state.data;
      if (data?.expires_in_seconds) {
        const refetchIn = Math.max(
          (data.expires_in_seconds - 60) * 1000,
          30000,
        );
        return refetchIn;
      }
      return 12 * 60 * 1000;
    },
  });
}

/**
 * API response wrapper for update endpoint
 */
interface UpdateAppResponse {
  app: App;
}

/**
 * Hook for updating an app (e.g., changing status).
 * Requires admin role.
 */
export function useUpdateApp() {
  const queryClient = useQueryClient();

  return useMutation<App, Error, { id: string; data: UpdateAppRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<UpdateAppResponse>(
        `miniapps/${id}/update/`,
        data,
      );
      return response.app;
    },
    onSuccess: (updatedApp) => {
      // Update the individual app cache
      queryClient.setQueryData([...APPS_QUERY_KEY, updatedApp.id], updatedApp);
      // Invalidate the list to refresh
      queryClient.invalidateQueries({
        queryKey: APPS_QUERY_KEY,
        exact: false,
      });
    },
  });
}
